import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  type Notification,
} from "../api/client";

const POLL_INTERVAL = 30_000; // 30 seconds

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Poll unread count
  const pollCount = useCallback(async () => {
    try {
      const count = await fetchUnreadCount();
      setUnread(count);
    } catch {
      // silently ignore — bell is non-critical
    }
  }, []);

  useEffect(() => {
    pollCount();
    const timer = setInterval(pollCount, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [pollCount]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const items = await fetchNotifications();
      setNotifications(items.slice(0, 20));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!open) loadNotifications();
    setOpen((v) => !v);
  };

  const handleClick = async (n: Notification) => {
    if (!n.read) {
      await markNotificationRead(n.id).catch(() => {});
      setUnread((c) => Math.max(0, c - 1));
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))
      );
    }
    setOpen(false);
    navigate(`/deals/${n.deal_id}`);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setUnread(0);
    setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
  };

  const timeAgo = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg text-bark-400 hover:text-bark-700 hover:bg-grove-100 transition-all duration-200"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-honey-600 text-white text-[10px] font-bold px-1 shadow-sm">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-grove-300 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-grove-50 border-b border-grove-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-bark-700">Notifications</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-[11px] text-forest-700 hover:text-forest-800 font-medium"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded text-bark-400 hover:text-bark-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[360px] overflow-y-auto divide-y divide-grove-100">
            {loading && (
              <div className="px-4 py-8 text-center text-sm text-bark-400">Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-bark-400">
                No notifications yet.
              </div>
            )}
            {!loading &&
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-grove-50 transition-colors ${
                    !n.read ? "bg-forest-50/30" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-forest-600" />
                    )}
                    <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                      {n.deal_name && (
                        <p className="text-[10px] font-medium text-bark-400 uppercase tracking-wide mb-0.5">
                          {n.deal_name}
                        </p>
                      )}
                      <p className="text-sm text-bark-700 leading-snug line-clamp-2">
                        {n.content}
                      </p>
                      <p className="text-[11px] text-bark-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
